import { motion } from "framer-motion";
import { ArrowUpRight, Headphones } from "lucide-react";
import { BOOKS } from "@/data/books";
import ChapterMarker from "@/components/ChapterMarker";

const EASE = [0.16, 1, 0.3, 1];

const BookCard = ({ book, index }) => (
  <motion.article
    data-testid={`book-card-${book.slug}`}
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, ease: EASE, delay: (index % 3) * 0.12 }}
    className="group flex flex-col"
  >
    <div className="book-scene mx-auto w-full max-w-[300px]">
      <div className="book-cover relative aspect-[2/3] overflow-hidden bg-abbey-ink">
        <img
          src={book.cover}
          alt={`${book.title} book cover`}
          loading="lazy"
          onError={(e) => {
            if (e.currentTarget.src !== book.fallbackCover) {
              e.currentTarget.src = book.fallbackCover;
            }
          }}
          className="h-full w-full object-cover"
          data-testid={`book-cover-${book.slug}`}
        />
      </div>
    </div>

    <div className="mt-10 flex flex-1 flex-col">
      <div className="flex items-baseline justify-between gap-4">
        <span className="font-cinzel text-xs tracking-[0.3em] text-abbey-rust">
          № {book.number}
        </span>
        <span
          className="font-cinzel text-sm font-semibold tracking-[0.15em] text-abbey-ink"
          data-testid={`book-price-${book.slug}`}
        >
          {book.price}
        </span>
      </div>

      <h3 className="mt-3 font-cormorant text-3xl font-semibold leading-tight text-abbey-ink md:text-4xl">
        {book.title}
      </h3>
      <p className="mt-1 font-cormorant text-lg italic leading-snug text-abbey-ink/60">
        {book.subtitle}
      </p>

      <p className="mt-5 flex-1 text-sm leading-relaxed text-abbey-ink/75">
        {book.description}
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid={`book-formats-${book.slug}`}>
        {book.formats.map((format) => (
          <span
            key={format}
            className="border border-abbey-ink/25 px-3 py-1 font-cinzel text-[10px] tracking-[0.2em] text-abbey-ink/70"
          >
            {format.toUpperCase()}
          </span>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <a
          data-testid={`buy-on-amazon-${book.slug}`}
          href={book.amazonUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-abbey-ink bg-abbey-ink px-7 py-3.5 font-cinzel text-[10px] font-semibold tracking-[0.25em] text-abbey-cream transition-colors duration-300 hover:border-abbey-gold hover:bg-abbey-gold hover:text-abbey-dark"
        >
          BUY ON AMAZON
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
        {book.formats.includes("Audible") && (
          <span className="inline-flex items-center gap-2 font-cinzel text-[10px] tracking-[0.2em] text-abbey-ink/55">
            <Headphones className="h-3.5 w-3.5 text-abbey-rust" strokeWidth={1.5} />
            ALSO ON AUDIBLE
          </span>
        )}
      </div>
    </div>
  </motion.article>
);

export default function BooksSection() {
  return (
    <section
      id="books"
      data-testid="books-section"
      className="relative bg-abbey-light px-6 py-24 md:px-12 md:py-32 lg:px-24 lg:py-40"
    >
      <div className="mx-auto max-w-7xl">
        <ChapterMarker number="01" eyebrow="Chapter One — The Shelf" title="The Books" />
        <div className="grid gap-x-10 gap-y-24 md:grid-cols-2 lg:grid-cols-3">
          {BOOKS.map((book, index) => (
            <BookCard key={book.slug} book={book} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
